// ============================================================
// CLUBHUB – Einstellungen finden
// ============================================================
// In ClubDesk muss man wissen, dass die Gläubiger-ID nirgends und
// die Termin-Erinnerung unter „Termine › Standard-Einstellungen“
// steht. Hier wird ueber Bereich, Register, Beschriftung, Hinweis
// und Schluessel gesucht — und der Treffer hebt das Feld im
// Register hervor (`?feld=`). Der Index steht im Arbeitsspeicher,
// weil das Register im Code steht: kein Netzwerkweg, kein Laden.
// ============================================================

import type { Permission } from "@/lib/permissions";
import { ALL_AREAS, registerErlaubt, tabPath } from "./registry";

export type SettingsHit = {
  id: string;
  label: string;
  /** Wo es steht: „Finanzen › Rechnungen und Mahnwesen“. */
  sub: string;
  href: string;
};

/** Ohne Akzente, ohne Grossschreibung, ohne Eszett — so wird gesucht. */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

type Entry = SettingsHit & { haystack: string; titel: string; areaKey: string; tabKey: string };

let index: Entry[] | null = null;

function buildIndex(): Entry[] {
  const eintraege: Entry[] = [];
  // Auch das eigene Konto: wer hier „Signatur“ sucht, findet die eigene.
  for (const area of ALL_AREAS) {
    for (const tab of area.tabs) {
      eintraege.push({
        id: `${area.key}/${tab.key}`,
        label: tab.title,
        sub: area.title,
        href: tabPath(area, tab),
        haystack: normalize(`${area.title} ${tab.title} ${tab.why}`),
        titel: normalize(tab.title),
        areaKey: area.key,
        tabKey: tab.key,
      });
      for (const field of tab.fields ?? []) {
        eintraege.push({
          id: field.key,
          label: field.label,
          sub: `${area.title} › ${tab.title}`,
          href: `${tabPath(area, tab)}?feld=${encodeURIComponent(field.key)}`,
          haystack: normalize(`${area.title} ${tab.title} ${field.section ?? ""} ${field.label} ${field.hint ?? ""} ${field.key}`),
          titel: normalize(field.label),
          areaKey: area.key,
          tabKey: tab.key,
        });
      }
    }
  }
  return eintraege;
}

/**
 * Alle Woerter muessen vorkommen, die Reihenfolge nicht: „zahl ziel“
 * findet das Zahlungsziel. Gefunden wird nur, was die Person auch
 * oeffnen darf.
 */
export function searchSettings(query: string, rechte: readonly Permission[], limit = 8): SettingsHit[] {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  index ??= buildIndex();
  const darf = (p: Permission) => rechte.includes(p);
  return index
    .filter((entry) => {
      const area = ALL_AREAS.find((a) => a.key === entry.areaKey)!;
      const tab = area.tabs.find((t) => t.key === entry.tabKey)!;
      return registerErlaubt(area, tab, darf);
    })
    .filter((entry) => terms.every((term) => entry.haystack.includes(term)))
    // Wer „Gläubiger“ sucht, will das Feld, nicht das Register, dessen
    // Erklaerung das Wort auch enthaelt: Treffer in der Bezeichnung zuerst.
    .map((entry) => ({ entry, rang: terms.every((term) => entry.titel.includes(term)) ? 0 : 1 }))
    .sort((a, b) => a.rang - b.rang)
    .map(({ entry }) => entry)
    .slice(0, limit)
    .map(({ id, label, sub, href }) => ({ id, label, sub, href }));
}
